class KunkPeekCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.btnPrev = null;
    this.btnNext = null;
    this.originalSlides = [];
    this.slides = [];
    this.realCount = 0;
    this.clonesPerSide = parseInt(this.dataset.clonesPerSide || '4', 10);
    this.scrollDebounce = null;
    this.animateTimer = null;
    this.activeIdx = -1;
    this.jumping = false;
    this.animating = false;
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('.kunk-peek-carousel__track');
    this.btnPrev = this.querySelector('.kunk-peek-carousel__btn--prev');
    this.btnNext = this.querySelector('.kunk-peek-carousel__btn--next');
    if (!this.track) return;
    this.originalSlides = Array.from(this.track.children).filter(c => !c.dataset.clone);
    this.realCount = this.originalSlides.length;

    if (this.realCount > 1) {
      this.setup();
    } else {
      this.hideButtons();
    }
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
    if (this.btnPrev) this.btnPrev.removeEventListener('click', this.handlePrev);
    if (this.btnNext) this.btnNext.removeEventListener('click', this.handleNext);
  }

  hideButtons() {
    if (this.btnPrev) this.btnPrev.style.display = 'none';
    if (this.btnNext) this.btnNext.style.display = 'none';
  }

  isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  getPaddingLeft() {
    return parseFloat(getComputedStyle(this.track).paddingLeft || 0);
  }

  setup() {
    if (this.track.querySelector('[data-clone="true"]')) return;

    const last = this.originalSlides.slice(-this.clonesPerSide);
    const first = this.originalSlides.slice(0, this.clonesPerSide);

    last.slice().reverse().forEach(s => {
      const c = s.cloneNode(true);
      c.dataset.clone = 'true';
      c.removeAttribute('id');
      this.track.insertBefore(c, this.track.firstChild);
    });

    first.forEach(s => {
      const c = s.cloneNode(true);
      c.dataset.clone = 'true';
      c.removeAttribute('id');
      this.track.appendChild(c);
    });

    this.slides = Array.from(this.track.children);

    const start = () => {
      this.scrollToIndex(this.clonesPerSide, false);
      this.updateActive();
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      if (this.btnPrev) this.btnPrev.addEventListener('click', this.handlePrev);
      if (this.btnNext) this.btnNext.addEventListener('click', this.handleNext);
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => requestAnimationFrame(start));
    } else {
      requestAnimationFrame(start);
    }
  }

  handleScroll() {
    if (this.jumping || this.animating) return;
    this.updateActive();
    clearTimeout(this.scrollDebounce);
    this.scrollDebounce = setTimeout(() => this.checkLoop(), 120);
  }

  checkLoop() {
    if (this.jumping || this.animating) return;
    const idx = this.getCurrentIndex();
    if (idx < this.clonesPerSide) {
      this.jumpToIndex(idx + this.realCount);
    } else if (idx >= this.clonesPerSide + this.realCount) {
      this.jumpToIndex(idx - this.realCount);
    }
  }

  /**
   * Returns the index of the slide that is currently "anchored" in the viewport.
   * - Desktop: the slide whose left edge is closest to the left edge of the visible area.
   * - Mobile: the slide whose center is closest to the center of the viewport.
   */
  getCurrentIndex() {
    if (!this.slides.length) return 0;
    const trackRect = this.track.getBoundingClientRect();
    const desktop = this.isDesktop();
    const refX = desktop
      ? trackRect.left + this.getPaddingLeft()
      : trackRect.left + trackRect.width / 2;

    let closest = 0;
    let closestDist = Infinity;
    for (let i = 0; i < this.slides.length; i++) {
      const r = this.slides[i].getBoundingClientRect();
      const slideRef = desktop ? r.left : r.left + r.width / 2;
      const d = Math.abs(slideRef - refX);
      if (d < closestDist) {
        closestDist = d;
        closest = i;
      }
    }
    return closest;
  }

  scrollToIndex(idx, smooth) {
    if (!this.slides[idx]) return;
    const slide = this.slides[idx];
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const offsetInScroll = (slideRect.left - trackRect.left) + this.track.scrollLeft;
    const desktop = this.isDesktop();
    const target = desktop
      ? offsetInScroll - this.getPaddingLeft()
      : offsetInScroll - (trackRect.width - slideRect.width) / 2;
    if (smooth) {
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = target;
    }
  }

  jumpToIndex(idx) {
    this.jumping = true;
    this.scrollToIndex(idx, false);
    requestAnimationFrame(() => {
      this.updateActive();
      this.jumping = false;
    });
  }

  updateActive() {
    const idx = this.getCurrentIndex();
    if (idx === this.activeIdx) return;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    this.activeIdx = idx;
  }

  getStep() {
    return this.isDesktop() ? 2 : 1;
  }

  animateTo(idx) {
    if (!this.slides[idx]) return;
    this.animating = true;
    const prevSnap = this.track.style.scrollSnapType;
    this.track.style.scrollSnapType = 'none';
    this.scrollToIndex(idx, true);
    clearTimeout(this.animateTimer);
    this.animateTimer = setTimeout(() => {
      this.track.style.scrollSnapType = prevSnap;
      this.animating = false;
      this.updateActive();
      this.checkLoop();
    }, 650);
  }

  handlePrev() {
    if (this.animating || this.jumping) return;
    const idx = this.getCurrentIndex();
    this.animateTo(idx - this.getStep());
  }

  handleNext() {
    if (this.animating || this.jumping) return;
    const idx = this.getCurrentIndex();
    this.animateTo(idx + this.getStep());
  }
}

if (!customElements.get('kunk-peek-carousel')) {
  customElements.define('kunk-peek-carousel', KunkPeekCarousel);
}
